import React, { useState, useEffect } from 'react';
import { db } from '../../utils/firebase-config';
import { collection, getDocs, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faFilePdf } from '@fortawesome/free-solid-svg-icons';
import { surveySteps } from "../Survey/Questions/Questions";
import generatePDF from '../PdfGenerator/PdfGenerator'; // Ensure this import path is correct
import { format } from 'date-fns'; // Import date-fns for date formatting
import { useNavigate } from 'react-router-dom'; // Import useNavigate hook
import updateUserProgress from '../Survey/userProgressTrack/updateUserProgress';


const CommunityFormSubmissions = ({ userId }) => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [currentStep, setCurrentStep] = useState(null); // State to store current step
  const navigate = useNavigate(); // Initialize useNavigate

  useEffect(() => {
    const fetchSubmissions = async () => {
      const submissionsRef = collection(db, 'users', userId, 'Community_Minded_Business_Concept_Form');
      const querySnapshot = await getDocs(submissionsRef);
      const submissionsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filter out empty submissions
      const filteredSubmissions = submissionsList.filter(submission => {
        const keys = Object.keys(submission);
        return keys.some(key => key !== 'createdAt' && key !== 'id');
      });

      // Sort submissions by createdAt
      const sortedSubmissions = filteredSubmissions.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setSubmissions(sortedSubmissions);
      setLoading(false);
    };

    const fetchCurrentStep = async () => {
      const userDocRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userDocRef);
      if (docSnap.exists()) {
        const userData = docSnap.data();
        if (userData.currentStep) {
          setCurrentStep(userData.currentStep);
        }
      }
    };

    fetchSubmissions();
    fetchCurrentStep();
  }, [userId]);

  const handleContinue = () => {
    if (currentStep) {
      navigate(`/survey/${currentStep}`);
    }
  };

  const handleCreateNewSubmission = async () => {
    const startStep = 12; // Starting step
    const newSubmissionDoc = await addDoc(collection(db, 'users', userId, 'Community_Minded_Business_Concept_Form'), { createdAt: new Date().toISOString() }); // Generate new submission ID
    await updateDoc(doc(db, 'users', userId), { currentStep: startStep });
    const newSubmissionId = newSubmissionDoc.id;
    localStorage.setItem('currentSubmissionId', newSubmissionId); // Store submission ID in localStorage

    navigate(`/survey/${startStep}`); // Redirect to the start of the form
  };

  const renderSubmissionDetails = (submission) => {
    return surveySteps
      .filter(step => step.area === 'Community-Minded Business Concept Form' && step.id !== 19)
      .map(step => {
        const stepAnswers = submission[step.stepTitle];

        return (
          <div key={step.id} className="mb-4">
            <h4 className="text-lg font-bold">{step.stepTitle.replace(/_/g, ' ')}</h4>
            {stepAnswers && Object.entries(stepAnswers).map(([question, answer], idx) => (
              <div key={idx} className="ml-4">
                {question.replace(/_/g, ' ')}: <strong>{typeof answer === 'object' ? JSON.stringify(answer) : answer}</strong>
              </div>
            ))}
          </div>
        );
      });
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      {submissions.map((submission, index) => (
        <div key={submission.id} className="mb-2 p-2 border rounded">
          <div className="flex justify-between items-center">
            <h3>
              Submission {index + 1} - {format(new Date(submission.createdAt), 'MMMM dd, yyyy HH:mm:ss')}
            </h3>
            <div className="flex">
              <button
                className="mr-2"
                onClick={() => generatePDF(submission, `Submission_${index + 1}_Details`)}
              >
                <FontAwesomeIcon icon={faFilePdf} /> Download PDF
              </button>
              <button onClick={() => setExpandedSubmission(expandedSubmission === submission.id ? null : submission.id)}>
                <FontAwesomeIcon icon={expandedSubmission === submission.id ? faChevronUp : faChevronDown} />
              </button>
            </div>
          </div>
          {expandedSubmission === submission.id && (
            <div>
              {renderSubmissionDetails(submission)}
              {localStorage.getItem('currentSubmissionId') === submission.id && (
                <div className="mt-4">
                  <button
                    onClick={handleContinue}
                    className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                  >
                    Continue
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
      <button
        onClick={handleCreateNewSubmission}
        className="mt-4 px-8 py-3 text-black bg-amber-500 font-semibold rounded-full hover:bg-amber-600 transition-colors"
      >
        Create New Submission
      </button>
    </div>
  );
};

export default CommunityFormSubmissions;